import { useCallback, useEffect, useState } from 'react';
import type { PersonSummary, TabletRecord } from './lib/credential';
import { cacheLogins, getTablet, listPeople } from './lib/credential';
import type { Session } from './lib/session';
import { allowedTabs, subscribe } from './lib/session';
import { api, ApiError, setTabletCredential } from './lib/api';
import type { PrintJob } from './lib/print';
import { subscribePrint } from './lib/print';
import Login from './screens/Login';
import RegisterTablet from './screens/RegisterTablet';
import Settings from './screens/Settings';
import Day from './screens/Day';
import Approvals from './screens/Approvals';
import Report from './screens/Report';
import PayRun from './screens/PayRun';
import Summary from './screens/Summary';
import Permits from './screens/Permits';
import ExpiringSoon from './components/ExpiringSoon';
import LoanRequests from './components/LoanRequests';
import PayRunApprovals from './components/PayRunApprovals';
import PrintPreview from './components/PrintPreview';
import Shell from './components/Shell';
import { startSync } from './lib/sync';
import { kvGet, kvSet } from './lib/store';
import { businessDateNow } from './lib/calendar';
import type { CostCode } from './lib/costcodes';
import { applyUpdate, startUpdates, useUpdateReady } from './lib/update';

/**
 * Three states, decided by what is in the vault:
 *
 *   not registered     → RegisterTablet. A tablet code, or the recovery code.
 *   registered         → Login. Everyone who can log in, then the PIN pad.
 *   session            → the app shell.
 *
 * A tablet is registered once and caches every login's PIN-wrapped credential
 * (refreshed whenever there is a signal), so anyone the owner has given a PIN
 * can use it. A reload always lands on Login: the session token is held in
 * memory only, so the tablet locks itself whenever the page reloads. That is
 * what you want on a device that gets passed around at shift change.
 */

type View = 'loading' | 'enrol' | 'login' | 'app';

interface Reference {
  today: string;
  plants: { id: string; code: string; name: string; cost_code_id?: string | null }[];
  miscCategories: { id: string; name: string; requires_plant: number }[];
  costCodes: CostCode[];
}

export default function App() {
  const [tablet, setTablet] = useState<TabletRecord | null>(null);
  const [people, setPeople] = useState<PersonSummary[]>([]);
  const [session, setSession] = useState<Session | null>(null);
  const [view, setView] = useState<View>('loading');
  const [tab, setTab] = useState('labour');
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [reference, setReference] = useState<Reference | null>(null);
  const [printJob, setPrintJob] = useState<PrintJob | null>(null);

  // Who can log in: from the cache first, then refreshed from the office when
  // there is a signal — a login created or a PIN reset on the owner's phone
  // reaches every tablet on its next load or unlock.
  const refreshPeople = useCallback(async (registered: TabletRecord | null) => {
    let list = await listPeople();
    setPeople(list);
    if (registered) {
      try {
        const result = await api.logins();
        await cacheLogins(result.logins);
        list = await listPeople();
        setPeople(list);
      } catch {
        /* no signal, or the tablet was removed: the cache stands, or is empty */
      }
    }
    return list;
  }, []);

  useEffect(() => {
    void (async () => {
      const registered = await getTablet();
      setTablet(registered);
      setTabletCredential(registered?.credential ?? null);
      await refreshPeople(registered);
      setView(registered ? 'login' : 'enrol');
    })();
  }, [refreshPeople]);

  useEffect(() => subscribe(setSession), []);
  useEffect(() => subscribePrint(setPrintJob), []);

  const refreshDevice = useCallback(async () => {
    const registered = await getTablet();
    setTablet(registered);
    setTabletCredential(registered?.credential ?? null);
    await refreshPeople(registered);
    setView(registered ? 'login' : 'enrol');
  }, [refreshPeople]);

  // The idle timer and the name chip both end up here.
  useEffect(() => {
    if (session) {
      setView('app');
      setTab((current) => {
        const allowed = allowedTabs(session);
        if (allowed.some((definition) => definition.id === current)) return current;
        return allowed[0]?.id ?? 'labour';
      });
      return;
    }
    if (view === 'app') {
      setSettingsOpen(false);
      setReference(null);
      void refreshDevice();
    }
  }, [session, view, refreshDevice]);

  // The business date and the reference lists come from the server, once per
  // session. With no signal the lists are the ones cached from the last
  // connection and the date is the device clock read in Asia/Colombo — see
  // lib/calendar.ts for why that beats a cached date. When the signal comes
  // back the session stops being offline and both are fetched afresh.
  const offline = session?.offline ?? false;
  useEffect(() => {
    if (!offline) setReference(null);
  }, [offline]);

  useEffect(() => {
    if (!session || reference) return;
    if (session.offline) {
      void kvGet<Omit<Reference, 'today'>>('reference').then((cached) => {
        // A copy cached before cost codes existed has none.
        if (cached) setReference({ ...cached, costCodes: cached.costCodes ?? [], today: businessDateNow() });
      });
      return;
    }
    api
      .reference()
      .then((result) => {
        const next = {
          today: result.today,
          plants: result.plants,
          miscCategories: result.miscCategories,
          costCodes: result.costCodes ?? [],
        };
        setReference(next);
        const { today: _today, ...lists } = next;
        void kvSet('reference', lists);
      })
      .catch(() => {
        /* the day screen shows its own error */
      });
  }, [session, reference]);

  // The outbox: sends what was entered with no signal, whenever there is one.
  const hasSession = session !== null;
  useEffect(() => (hasSession ? startSync() : undefined), [hasSession]);

  // A new version: taken at once on the login screen, offered while signed in.
  const updateReady = useUpdateReady();
  useEffect(() => { startUpdates(); }, []);
  useEffect(() => {
    if (updateReady && !hasSession) void applyUpdate();
  }, [updateReady, hasSession]);

  if (view === 'loading') {
    return <div className="wrap"><div className="empty">Loading…</div></div>;
  }

  if (!tablet) {
    return <RegisterTablet onDone={() => void refreshDevice()} />;
  }

  if (view === 'login' || !session) {
    return (
      <Login
        people={people}
        tabletLabel={tablet.label}
        onRefresh={() => void refreshDevice()}
      />
    );
  }

  const isDayTab = tab === 'labour' || tab === 'diesel' || tab === 'misc';

  return (
    <>
    <div className={printJob ? 'behind-preview' : undefined}>
    <Shell
      session={session}
      tab={tab}
      onTab={(next) => {
        setSettingsOpen(false);
        setTab(next);
      }}
      onSettings={() => setSettingsOpen((open) => !open)}
      settingsOpen={settingsOpen}
    >
      {updateReady && (
        <div className="banner b-warn" style={{ justifyContent: 'space-between' }}>
          <span>A new version of the app is ready.</span>
          <button type="button" className="btn sm" style={{ minWidth: 0 }} onClick={() => void applyUpdate()}>
            Update now
          </button>
        </div>
      )}
      {settingsOpen ? (
        <Settings
          currentUserId={session.user.id}
          tabletId={tablet.tabletId}
          onBack={() => setSettingsOpen(false)}
          onPeopleChanged={() => void refreshPeople(tablet)}
        />
      ) : tab === 'pay' && reference ? (
        <PayRun
          isOwner={session.user.role === 'owner'}
          userName={session.user.name}
          userId={session.user.id}
          today={reference.today}
        />
      ) : tab === 'summary' && reference ? (
        <Summary userName={session.user.name} costCodes={reference.costCodes} />
      ) : tab === 'report' && reference ? (
        <Report today={reference.today} reference={reference} userName={session.user.name} />
      ) : tab === 'permits' && reference ? (
        <Permits session={session} today={reference.today} />
      ) : tab === 'approvals' && reference ? (
        <>
          <ExpiringSoon variant="card" onGo={() => setTab('permits')} />
          {/* Loan requests from the office wait here too, not only under Pay run. */}
          <LoanRequests />
          {/* Pay runs waiting for him: approve only after reading the summary. */}
          <PayRunApprovals userName={session.user.name} />
          <Approvals reference={reference} today={reference.today} />
        </>
      ) : isDayTab && reference ? (
        <>
          {tab === 'labour' && <ExpiringSoon variant="strip" />}
          <Day
            session={session}
            tab={tab}
            today={reference.today}
            reference={reference}
          />
        </>
      ) : !reference ? (
        <div className="empty">Loading…</div>
      ) : (
        <Placeholder tab={tab} />
      )}
    </Shell>
    </div>
    {printJob && <PrintPreview job={printJob} />}
    </>
  );
}

/**
 * Steps 5 onwards. Each becomes a real screen built from the matching part of
 * prototype.html.
 */
function Placeholder({ tab }: { tab: string }) {
  const next: Record<string, string> = {
  };

  return (
    <div className="card">
      <h3>Not built yet</h3>
      <div className="sub">{next[tab] ?? 'Coming in a later step.'}</div>
    </div>
  );
}

export { ApiError };
